import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Alert, Box, Button, Chip, LinearProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../context/AuthContext';
import { v4API } from '../../Api';
import { errorMessage } from './common';

const V4Context = createContext(null);
const tabs = [
  ['/v4', 'Обзор'], ['/v4/orders', 'Ордера'], ['/v4/merchants', 'Мерчанты'], ['/v4/processings', 'Процессинги'],
  ['/v4/telegram-bot', 'Telegram-бот'],
  ['/v4/crypto-addresses', 'Криптоадреса'],
  ['/v4/site-balances', 'Балансы сайтов'],
  ['/v4/payouts', 'Выплаты мерчантам'], ['/v4/processing-debts', 'Долги процессингов'], ['/v4/telegram-users', 'Telegram-пользователи'], ['/v4/routes', 'Маршруты'], ['/v4/tariffs', 'Тарифы'], ['/v4/queues', 'Очереди'], ['/v4/secrets', 'Секреты'], ['/v4/system', 'Система'],
];

export function useV4() {
  const value = useContext(V4Context);
  if (!value) throw new Error('V4 context is required');
  return value;
}

export default function V4Layout() {
  const { user, isAuth } = useAuth();
  const location = useLocation();
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [systemError, setSystemError] = useState('');
  const generation = useRef(0);
  const allowed = isAuth && user?.role === 'admin';
  const refreshSystem = useCallback(async () => {
    if (!allowed) return null;
    const requestId = ++generation.current;
    setLoading(true);
    setSystemError('');
    try {
      const state = await v4API.system();
      if (requestId === generation.current) setSystem(state);
      return state;
    } catch (error) {
      if (requestId === generation.current) { setSystem(null); setSystemError(errorMessage(error)); }
      return null;
    } finally {
      if (requestId === generation.current) setLoading(false);
    }
  }, [allowed]);
  useEffect(() => {
    refreshSystem();
    return () => { generation.current += 1; };
  }, [refreshSystem]);
  const context = useMemo(() => ({ system, loading, systemError, refreshSystem }), [system, loading, systemError, refreshSystem]);
  if (!isAuth) return <Navigate to="/auth" replace />;
  if (!allowed) return <Alert severity="error" action={<Button component={Link} to="/" color="inherit">На главную</Button>}>Раздел V4 доступен только администратору.</Alert>;
  const active = tabs.slice(1).find(([path]) => location.pathname === path || location.pathname.startsWith(`${path}/`))?.[0] || '/v4';
  return (
    <V4Context.Provider value={context}>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 3, color: 'white', background: 'linear-gradient(120deg, #3730a3, #6366f1)' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
            <Stack direction="row" alignItems="center" spacing={2}>
              <LayersOutlinedIcon sx={{ fontSize: 36 }} />
              <Box><Typography variant="h4" component="h1">Платежи V4</Typography><Typography variant="body2" sx={{ opacity: 0.85 }}>Ордера, процессинги и доставка событий</Typography></Box>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip label={system?.ready ? 'Готова к работе' : 'Требуется проверка'} size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.18)' }} />
              <Button startIcon={<RefreshIcon />} color="inherit" onClick={refreshSystem} disabled={loading}>Обновить</Button>
            </Stack>
          </Stack>
        </Box>
        <Tabs value={active} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }} aria-label="Разделы V4">
          {tabs.map(([path, label]) => <Tab key={path} value={path} label={label} component={Link} to={path} />)}
        </Tabs>
        {loading && <LinearProgress aria-label="Проверка состояния V4" sx={{ mb: 2 }} />}
        {systemError && <Alert severity="error" sx={{ mb: 2 }}>{systemError}</Alert>}
        {system && !system.ready && active !== '/v4/system' && <Alert severity="warning" sx={{ mb: 2 }} action={<Button component={Link} to="/v4/system" color="inherit">Настроить</Button>}>V4 ещё не готова. Старая система продолжает работать независимо.</Alert>}
        <Outlet />
      </Box>
    </V4Context.Provider>
  );
}
